/* -------------------------------------------------------------------------
   ChatBotModal.jsx — tiny side-chat that talks to /api/ChatBot/ask
--------------------------------------------------------------------------- */
import React, { useState, useRef, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, IconButton, TextField,
    List, ListItem, ListItemText, Button, Paper, CircularProgress,
    Typography, Box
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { styled, keyframes } from "@mui/system";
import PropTypes from "prop-types";
import ChatBotService from "../../../Services/chatbot.service";
import Lottie from "lottie-react";
import chatBotAnimation from "../../../assests/images/Animations/chatBotAnimation.json";
import { dir } from "i18next";

/* ▸ bubbles                                                     */
const AssistantPaper = styled(Paper)({
    background: "#F2F4FF",
    padding: "8px 8px 8px 0px",
    margin: '0 -15px 0 0',
    borderRadius: 10,
    display: "inline-block",
    maxWidth: "80%",
    textAlign: "right",
    fontSize: 20,
    fontWeight: 900,
    "& .MuiTypography-root": { color: "F2F4FF", fontSize: 16, fontWeight: 600, direction: "ltr", textAlign: "left" }
});

const rtlStyles = {
    direction: "rtl",
    textAlign: "right",
    "& .MuiTypography-root": { direction: "rtl", textAlign: "right" }
};

const ltrStyles = {
    direction: "ltr",
    textAlign: "left",
    "& .MuiTypography-root": { direction: "ltr", textAlign: "left" }
};


const UserPaper = styled(Paper)({
    background: "#304FFF",
    color: "white",
    padding: 8,
    borderRadius: 10,
    maxWidth: "80%",
    textAlign: "right",
    margin: "0 0 0 10px",
    fontSize: 20,
    fontWeight: 900,
    "& .MuiTypography-root": { color: "white", fontSize: 16, fontWeight: 600 }
});

/* ▸ animated dots for loading                                    */
const dots = keyframes({
    "0%": { content: '""' },
    "33%": { content: '". "' },
    "66%": { content: '". ."' },
    "100%": { content: '". . ."' }
});

const LoadingDots = styled("span")({
    "&::after": {
        display: "inline-block",
        animation: `${dots} 1s infinite steps(3)`,
        whiteSpace: "pre"
    }
});

/* ▸ main component                                               */
export default function ChatBotModal({ open, onClose, accountantId, token, t }) {
    const [messages, setMessages] = useState([]);      // {role,text,loading?}
    const [input, setInput] = useState("");
    const [asking, setAsking] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    /* -- helper: add assistant reply one character at a time ------ */
    const typeWriter = (fullText) => {
        let idx = 0;
        setMessages(prev => [...prev, { role: "assistant", text: "" }]);
        const timer = setInterval(() => {
            setMessages(prev => {
                const copy = [...prev];
                copy[copy.length - 1].text = fullText.slice(0, idx + 1);
                return copy;
            });
            idx += 1;
            if (idx >= fullText.length) clearInterval(timer);
        }, 25);                                         // typing speed (ms)
    };

    /* -- submit ---------------------------------------------------- */
    const sendQuestion = async () => {
        const q = input.trim();
        if (!q || asking) return;

        setMessages(prev => [...prev, { role: "user", text: q }]);
        setInput("");
        setAsking(true);

        /* show loading dots bubble */
        setMessages(prev => [...prev, { role: "assistant", loading: true }]);

        try {
            const resp = await ChatBotService.askQuestion(accountantId, q, token);
            if (!resp.ok) throw new Error(await resp.text());
            const { answer } = await resp.json();

            /* remove the loading bubble */
            setMessages(prev => prev.filter(m => !m.loading));

            /* type the answer */
            typeWriter(answer);
        } catch (err) {
            setMessages(prev => prev.filter(m => !m.loading));
            typeWriter("⚠ " + t('ChatResponseError'));
        } finally {
            setAsking(false);
        }
    };

    const handleKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendQuestion();
        }
    };

    const rtlTf = {
        direction: "ltr", textAlign: "left",
        "& .MuiInputBase-input": { direction: "rtl", textAlign: "right" }
    };

    const ltrTf = {
        direction: "ltr", textAlign: "left",
        "& .MuiInputBase-input": { direction: "ltr", textAlign: "left" }
    };


    const isRtl = str =>
        /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFD\uFE70-\uFEFC]/.test(str);

    return (
        <Dialog
            anchor="right"
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    height: "80vh",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column"
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: "#304FFF",
                    color: "white",
                    py: 1,
                    px: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative"
                }}
            >
                {t("ChatBot")}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 4,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "white"
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    bgcolor: "#FBFBFD",
                    px: 2,
                    direction: "rtl"
                }}
            >
                {messages.length === 0 ? (
                    <Box
                        sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.disabled"
                        }}
                    >
                        <Lottie
                            animationData={chatBotAnimation}
                            loop
                            autoplay
                            style={{ width: 140, height: 140, marginBottom: 8 }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600, textAlign: "center" }}>
                            {t("TypeAnyQuestionYouWant")}
                        </Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {messages.map((m, i) => (
                            <ListItem
                                key={i}
                                sx={{ justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}
                            >
                                {m.role === "assistant" ? (
                                    <AssistantPaper
                                        elevation={0}
                                        sx={isRtl(m.text) ? ltrStyles : rtlStyles}
                                    >
                                        {m.loading ? <LoadingDots /> : <ListItemText secondary={m.text} />}
                                    </AssistantPaper>
                                ) : (
                                    <UserPaper
                                        elevation={1}
                                        sx={{
                                            ...(isRtl(m.text) ? ltrStyles : rtlStyles),
                                            "& .MuiTypography-root": { color: "white" }
                                        }}
                                    >
                                        <ListItemText secondary={m.text} />
                                    </UserPaper>
                                )}
                            </ListItem>
                        ))}
                        <div ref={bottomRef} />
                    </List>
                )}
            </DialogContent>


            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    sendQuestion();
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row-reverse",   /* input right, button left */
                        padding: 8,
                        gap: 40,
                    }}
                >
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        size="small"
                        placeholder={t("TypeYourQuestion")}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKey}
                        sx={{ width: "390px", ...(input && !isRtl(input) ? rtlTf : ltrTf) }}
                    />
                    <Button
                        variant="contained"
                        disabled={asking || !input.trim()}
                        onClick={sendQuestion}
                        sx={{ minWidth: 48, bgcolor: "#304FFF" }}
                    >
                        {asking
                            ? <CircularProgress size={20} sx={{ color: "white" }} />
                            : <SendIcon sx={{ transform: "scaleX(-1)" }} />}
                    </Button>
                </div>
            </form>
        </Dialog>
    );
}

ChatBotModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    accountantId: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired
};
